import { FC, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { CallIntelMutations, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import {
  callIntelKpiUtils,
  generateTrackingId,
  toTimePeriodType,
  URLS,
  useCallIntelDefaultsShallowStore,
  useCallIntelNavigator,
} from '@frontend/call-intel-core';
import { AnimatedBadge } from '@frontend/components';
import { i18next, useTranslation } from '@frontend/i18n';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { Page } from '@frontend/page';
import { pendo } from '@frontend/tracking';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text, useAlert } from '@frontend/design-system';
import { CallIntelServiceQualityFilters } from '../components/call-intelligence/filters/organisms/call-intel-service-quality-filters';
import { FiltersSection } from '../components/call-intelligence/filters-section';
import { useActiveFilters } from '../components/call-intelligence/hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../components/call-intelligence/hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../components/call-intelligence/hooks/use-call-intel-locations';
import { useCallIntelShallowStore } from '../components/call-intelligence/hooks/use-call-intel-store';
import { KpiSummary } from '../components/call-intelligence/kpi-summary';
import { SubViewHeader } from '../components/call-intelligence/sub-view-header';
import { CallRecordsTable } from '../components/call-intelligence/tables/call-records-table';
import { ScrollClassTrigger } from '../components/scroll-class-trigger';
import { URLs } from '../constants';
import { usePreviousTimePeriodLabel } from '../hooks/use-previous-time-period-label';
import { callIntelligenceUtils } from '../utils/call-intelligence';
import { callIntelResponsiveStyles } from './common-styles';

type Props = {
  type: CallIntelTypes.ServiceQualityFlagEnum;
};

const getServiceIssuePageDescription = (type: CallIntelTypes.ServiceQualityFlagEnum, entity: string): string => {
  switch (type) {
    case CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT:
      return i18next.t(
        'Detects when a {{entity}} expresses concern about treatment provided by your office during the call.',
        {
          entity,
          ns: 'analytics',
        }
      );
    case CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER:
      return i18next.t(
        'Detects when a {{entity}} mentions trying to reach the office multiple times or not receiving an expected callback.',
        {
          entity,
          ns: 'analytics',
        }
      );
    case CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION:
      return i18next.t(
        'Detects when a {{entity}} asks to speak with a manager or suggests a higher-up should be involved.',
        {
          entity,
          ns: 'analytics',
        }
      );
    default:
      return '';
  }
};

export const CallIntelServiceIssues: FC<React.PropsWithChildren<Props>> = ({ type }: Props) => {
  const { t } = useTranslation('analytics');

  const alert = useAlert();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const { pageLabels } = useCallIntelDefaultsShallowStore('pageLabels');
  const { lastUsedVertical: vertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { hasValidSubView } = useCallIntelNavigator();

  const { isDemoModal, isDemoAccount } = useCallIntelDemoFlags();

  const { filterHintText: currentPeriodLabel, subView } = useCallIntelShallowStore('filterHintText', 'subView');

  const { filters } = useActiveFilters();
  const headerRef = useRef<HTMLDivElement | null>(null);

  const { isMultiLocation, locations } = useCallIntelLocations();

  const previousTimePeriodLabel = usePreviousTimePeriodLabel(currentPeriodLabel);

  const pageLabel = pageLabels?.serviceQualityFlags?.[type] ?? '';

  const pageSubtitle = useMemo(
    () =>
      `${
        isMultiLocation && filters.locations?.length
          ? filters.locations.length === Object.keys(locations).length
            ? t('all locations, ')
            : filters.locations.length === 1
            ? t('1 location, ')
            : t('{{count}} locations, ', { count: filters.locations.length })
          : ''
      }${currentPeriodLabel}`,
    [currentPeriodLabel, filters.locations, isMultiLocation, locations, t]
  );

  const drillDownFilters = callIntelligenceUtils.getDrillDownFilterKeys({ id: type, type: 'service-quality' });

  const {
    mutate: getServiceQualityStats,
    isLoading: isStatsLoading,
    data: stats,
  } = CallIntelMutations.useGetServiceQualityStats({
    onError: () => {
      alert.error(t("Couldn't fetch service quality stats. Please try again."));
    },
  });

  const serviceQualityStats: CallIntelTypes.KpiItem[] = useMemo(() => {
    const currentSummaries = stats?.currentStats?.serviceQualityStats ?? [];
    const previousSummaries = stats?.previousStats?.serviceQualityStats ?? [];

    const currentAnalyzedCalls = stats?.currentStats?.analyzedCallsCount ?? 0;
    const previousAnalyzedCalls = stats?.previousStats?.analyzedCallsCount ?? 0;

    const currentFlagSummary = currentSummaries.find((s) => s.serviceQualityIssue === type);
    const previousFlagSummary = previousSummaries.find((s) => s.serviceQualityIssue === type);

    const currentFlagCallCount = currentFlagSummary?.callsCount ?? 0;

    const currentServiceIssueCallCount = currentFlagSummary?.callsCount ?? 0;
    const previousServiceIssueCallCount = previousFlagSummary?.callsCount ?? 0;

    const currentServiceIssueRate = callIntelKpiUtils.toPercent(currentServiceIssueCallCount, currentAnalyzedCalls);
    const previousServiceIssueRate =
      previousAnalyzedCalls > 0
        ? callIntelKpiUtils.toPercent(previousServiceIssueCallCount, previousAnalyzedCalls)
        : undefined;

    const serviceIssueRateTrend = callIntelKpiUtils.computeRelativeChange({
      currentBaseCount: currentAnalyzedCalls,
      currentPercent: currentServiceIssueRate,
      previousBaseCount: previousAnalyzedCalls,
      previousPercent: previousServiceIssueRate,
    });

    const partialNote =
      stats?.currentStats?.isPartial && stats?.previousStats?.isPartial
        ? t('*Partial comparison. Data for selected and previous period are limited.')
        : stats?.currentStats?.isPartial
        ? t('*Partial comparison. Current time period is ongoing.')
        : stats?.previousStats?.isPartial
        ? t('*Partial comparison. Limited data in previous period.')
        : undefined;

    return [
      {
        id: 'callCount',
        title: t('Count of Calls'),
        value: currentFlagCallCount,
        format: 'integer',
        subtext: pageLabel,
      },
      {
        id: 'serviceIssueRate',
        title: t('Service Issue Rate'),
        value: callIntelKpiUtils.formatPercentWithPrecision(currentServiceIssueRate),
        format: 'percent',
        subtext: t('Out of {{callsAnalyzed}} Calls Analyzed', { callsAnalyzed: currentAnalyzedCalls }),
        noCallsToAnalyze: currentAnalyzedCalls === 0 || previousAnalyzedCalls === 0,
        trend: {
          ...serviceIssueRateTrend,
          polarity: 'downGood',
          tooltip: t('{{trend}} {{percentageChange}}% since {{timePeriodLabel}}.', {
            trend: serviceIssueRateTrend.change === 'increased' ? 'Up' : 'Down',
            timePeriodLabel: previousTimePeriodLabel,
            percentageChange: serviceIssueRateTrend.percentageChange,
          }),
          partialNote,
        },
      },
    ];
  }, [stats]);

  useEffect(() => {
    const hasLocations = !!filters?.locations?.length;
    const hasDates = !!(filters?.startDate && filters?.endDate);

    if (hasLocations && hasDates && currentPeriodLabel) {
      const payload = CallIntelUtils.getServiceQualityStatsPayload({
        filters: { ...filters, serviceQualityFlags: [type] },
        dateRangeParams: { timePeriod: toTimePeriodType(currentPeriodLabel) },
      });

      getServiceQualityStats(payload as CallIntelTypes.GetServiceQualityStatsIO['input']); //TODO: Fix type casting when migrate filters to new schema-gen
    }
  }, [filters.locations, currentPeriodLabel]);

  useEffect(() => {
    // If search params exist, allow useInitCallIntelFilters to hydrate the store first
    if (!hasValidSubView(subView)) {
      navigate({ to: URLS.CALL_INTEL_MAIN_PAGE });
      return;
    }

    pendo.track('service_drilldown_page', { type: type?.toLowerCase() });
    // Only depend on primitive values, not object references to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidSubView, search, subView?.type, subView?.id, navigate, type]);

  return (
    <>
      {/* This is used to apply shadow class on header in small mode on scroll */}
      <ScrollClassTrigger targetRef={headerRef} outOfViewClassName='sticky-shadow' />
      <Page maxWidth='100%' css={callIntelResponsiveStyles.page}>
        <SubViewHeader
          headerRef={headerRef}
          title={pageLabel}
          subtitle={t('Showing results for {{time}}', { time: pageSubtitle })}
          subtitleExtra={
            <Text color='subdued' css={styles.descriptionText}>
              {getServiceIssuePageDescription(type, vertical !== Vertical.VET ? t('patient') : t('client'))}
            </Text>
          }
          breadcrumbs={[
            {
              label: isDemoModal ? '' : t('Analytics'),
              to: '/analytics/dashboard',
            },
            {
              label: t('Call Intelligence'),
              to: URLs.CALL_INTEL_MAIN_PAGE,
            },
            {
              label: pageLabel,
            },
          ]}
          feedbackTrackingId={generateTrackingId({ page: 'service-issues', context: 'general-feedback-btn-click' })}
          titleExtra={<AnimatedBadge label={t('Beta')} animated={false} bgColor={theme.colors.indigo50} />}
        />
        <Page.Body>
          <FiltersSection>
            <CallIntelServiceQualityFilters isLoading={isStatsLoading}>
              <CallIntelServiceQualityFilters.Filters />
              <CallIntelServiceQualityFilters.Actions />
            </CallIntelServiceQualityFilters>
          </FiltersSection>

          <section css={callIntelResponsiveStyles.contentWrapper}>
            <KpiSummary isLoading={isStatsLoading} items={serviceQualityStats} css={styles.kpiSummary} />

            <CallRecordsTable
              drillDownFilters={drillDownFilters}
              isDemoAccount={isDemoAccount}
              hideFilters={['serviceQualityFlags']}
              mobileViewHeight={'min(450px, 60vh)'}
              showOnlyTrayFilters
            />
          </section>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  descriptionText: css`
    margin-top: ${theme.spacing(1)};
  `,

  kpiSummary: css`
    margin-top: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(3)};
  `,
};
